import styled from 'styled-components'
import { media } from '../../../utils/mediaQuery'

export const TabsStickyRoot = styled.div`
  position: relative;
`

export const StickySentinel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
  visibility: hidden;
`

export const TabsContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-shrink: 0;
  gap: 6px;
  padding: ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid transparent;
  background: ${({ theme }) => theme.colors.background.primary};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &[data-stuck='true'] {
    border-bottom-color: ${({ theme }) => theme.colors.border.light};
  }

  /* Hide scrollbar on mobile but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.border.light};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary}40;
    border-radius: 2px;
  }

  ${media.tabletDown} {
    padding: ${({ theme }) => theme.spacing.medium};
    gap: 6px;

    /* Hide scrollbar completely on mobile */
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

export const TabButton = styled.button<{
  $active: boolean
  $disabled?: boolean
  $color?: string
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid
    ${({ theme, $active, $disabled, $color }) =>
      $disabled
        ? theme.colors.border.light
        : $active
          ? $color || theme.colors.primary
          : theme.colors.border.light};
  border-radius: 6px;
  background: ${({ theme, $active, $disabled, $color }) =>
    $disabled
      ? theme.colors.background.secondary
      : $active
        ? ($color || theme.colors.primary) + '10'
        : theme.colors.background.secondary};
  color: ${({ theme, $active, $disabled, $color }) =>
    $disabled
      ? theme.colors.text.tertiary
      : $active
        ? $color || theme.colors.primary
        : theme.colors.text.primary};
  font-size: 10px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  outline: none;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &:hover {
    background: ${({ theme, $active, $disabled, $color }) =>
      $disabled
        ? theme.colors.background.secondary
        : $active
          ? ($color || theme.colors.primary) + '15'
          : theme.colors.background.tertiary};
    border-color: ${({ theme, $disabled, $color }) =>
      $disabled ? theme.colors.border.light : $color || theme.colors.primary};
  }

  /* Disable hover on touch devices */
  @media (hover: none) {
    &:hover {
      background: ${({ theme, $active, $disabled, $color }) =>
        $disabled
          ? theme.colors.background.secondary
          : $active
            ? ($color || theme.colors.primary) + '10'
            : theme.colors.background.secondary};
      border-color: ${({ theme, $active, $disabled, $color }) =>
        $disabled
          ? theme.colors.border.light
          : $active
            ? $color || theme.colors.primary
            : theme.colors.border.light};
    }
  }

  ${media.tabletDown} {
    padding: 6px 10px;
    font-size: 11px;
    gap: 5px;
    /* Ensure minimum touch target */
    min-height: 36px;
  }
`

export const TabBadge = styled.span<{
  $color?: string
  $isEmpty?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: ${({ theme, $color, $isEmpty }) =>
    $isEmpty
      ? theme.colors.background.tertiary || '#f3f4f6'
      : $color
        ? $color + '18'
        : theme.colors.primaryLight};
  color: ${({ theme, $color, $isEmpty }) =>
    $isEmpty
      ? theme.colors.text.tertiary || '#aaa'
      : $color || theme.colors.primary};
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
`
